<script setup lang="ts">
import AppContainer from '@apparatix/library/AppContainer.vue';
import { onMounted, ref, computed, nextTick } from 'vue';
import DataTable, { DataTableRowClickEvent, DataTableFilterEvent, DataTableSortEvent, DataTablePageEvent } from 'primevue/datatable';
import Column from 'primevue/column';
import FormSkeleton from '@apparatix/Components/FormSkeleton.vue';
import MultiSelect from 'primevue/multiselect';
import Button from 'primevue/button';
import {
  useActivities,
  useBuildDefaultColumns,
  useColumnConfig,
  useConfirmUnsavedChanges,
  useContacts,
  useIndustryCodes,
  useStringFunctions,
  useToastNotifications,
} from '@apparatix/composables';
import { useCreateActivity } from '@apparatix/composables/useCreateActivity';
import ToggleSwitch from 'primevue/toggleswitch';
import { useContactStore } from '@apparatix/stores/useContactStore';
import { useOpportunityStore } from '@apparatix/stores/useOpportunityStore';
import { useOpportunityCreationStore } from '@apparatix/stores/useOpportunityCreationStore';
import Badge from 'primevue/badge';
import Modal from '@apparatix/Components/Modal.vue';
import CreateContact from './CreateContact.vue';
import { ContactOverview, ContactPayload } from '@apparatix/types/Contacts';
import { type LazyLoadEvent } from '@apparatix/types/Account';
import Avatar from 'primevue/avatar';
import Menu from 'primevue/menu';
import { useConfirm } from 'primevue/useconfirm';
import ConfirmDialog from 'primevue/confirmdialog';
import ColumnDisplaySelector from '@apparatix/library/ColumnDisplaySelector.vue';
import { button as greenButtonTheme } from '@apparatix/theme/Green';
import HeightOptimizer from './HeightOptimizer.vue';
import CreateCampaignWizard from '@apparatix/leads/CreateCampaignWizard.vue';
import CombineContactsWizard from './combineContacts/CombineContactsWizard.vue';
import { useRouter } from 'vue-router';

const store = useContactStore();
const opportunityStore = useOpportunityStore();
const creationStore = useOpportunityCreationStore();
const {
  addressToString, createContact, emailsToString, loadContactsHome, loadContacts, deleteContact,
} = useContacts();
const { postActivity, postEmailActivityUnlessSynced } = useActivities();
const { showInfo, showSuccess } = useToastNotifications();
const confirm = useConfirm();
const router = useRouter();
const dt = ref();

const { addActivityComponent, showModal } = useCreateActivity();

const { isEmptyString } = useStringFunctions();

const defaultColumns = useBuildDefaultColumns([
  ['id', 'Id', false],
  ['fullName', 'Name'],
  ['company', 'Company'],
  ['ownerName', 'Owner'],
  ['address', 'Address'],
  ['emails', 'Email'],
  ['phoneNumbers', 'Phone Number', false],
  ['lastActivityDate', 'Last Activity', false],
]);

const {
  handleColumnOrderChange,
  handleVisibleColumnChange,
  visibleColumns,
  columnOrder,
} = useColumnConfig({
  visibleItemsKey: 'crm:contacts:visible-columns',
  columnOrderKey: 'crm:contacts:column-order',
  ref: dt,
  defaultColumns: defaultColumns,
});

const showCreateContactModal = ref(false);
const isSavingContact = ref(false);

const { industryCodeRequired } = useIndustryCodes();

const onRowClick = (e: DataTableRowClickEvent) => {
  store.selectedContact = e.data;
  router.push({ path: `/crm/contacts/detail/contact/${e.data.id}` });
};

const exportCSV = () => {
  dt.value.exportCSV();
};

onMounted(() => loadContactsHome());

const rowsPerPageOptions = computed(() => {
  const base = [100, 250, 500, 1000];
  // Append an "all" option only when the total exceeds the largest preset,
  // then de-duplicate and sort so the dropdown never shows 0 or out-of-order values.
  const options = store.totalContactRecords > base[base.length - 1]
    ? [...base, store.totalContactRecords]
    : base;
  return [...new Set(options)].sort((a, b) => a - b);
});

const tableFiltersSortField = ref<string | ((item: any) => string) | null>(null);
const tableFiltersSortOrder = ref<number | null>(null);

const onPage = (event: DataTablePageEvent) => {
  const lazyEvt: LazyLoadEvent = {
    first: event.first,
    pageSize: event.rows,
    sortField: tableFiltersSortField.value,
    sortOrder: tableFiltersSortOrder.value,
    filters: store.tableFilters,
  };
  loadContacts(lazyEvt);
};

const onSort = (event: DataTableSortEvent) => {
  tableFiltersSortField.value = event.sortField;
  tableFiltersSortOrder.value = event.sortOrder;

  const lazyEvt: LazyLoadEvent = {
    first: 0,
    pageSize: event.rows,
    sortField: event.sortField,
    sortOrder: event.sortOrder,
    filters: store.tableFilters,
  };
  loadContacts(lazyEvt);
};

const DEBOUNCE_MS = 400;
let filterDebounceTimer: number | undefined;

const onFilter = (event: DataTableFilterEvent) => {
  store.tableFilters = { ...(event.filters as any) };

  const lazyEvt: LazyLoadEvent = {
    first: 0,
    pageSize: event.rows,
    sortField: tableFiltersSortField.value,
    sortOrder: tableFiltersSortOrder.value,
    filters: store.tableFilters,
  };

  if (filterDebounceTimer) window.clearTimeout(filterDebounceTimer);
  filterDebounceTimer = window.setTimeout(() => {
    loadContacts(lazyEvt);
  }, DEBOUNCE_MS);
};

const clearFilters = () => {
  store.resetTableFilters();
  tableFiltersSortField.value = null;
  tableFiltersSortOrder.value = null;
  const initialEvent: LazyLoadEvent = {
    first: 0,
    pageSize: store.rowsPerPage,
    sortField: null,
    sortOrder: null,
    filters: store.tableFilters,
  };
  loadContacts(initialEvent);
};

const onAddContactClick = async () => {
  showCreateContactModal.value = true;
};

const closeCreateContactModal = () => {
  showCreateContactModal.value = false;
};

const onContactCreated = async (payload: ContactPayload) => {
  isSavingContact.value = true;
  const { id, contact } = await createContact(payload);
  if (!Array.isArray(store.contacts)) {
    store.contacts = [];
  }

  const contactToAdd: Partial<ContactOverview> = {
    id: id,
    fullName: `${payload.first_name ?? ''} ${payload.last_name ?? ''}`,
    ownerId: contact.user_id,
    ownerName: contact.ownerName,
    address: payload.addresses?.length ? addressToString(payload.addresses[0]) : undefined,
    emails: payload.address_emails?.length ? emailsToString(payload.address_emails) : undefined,
  };

  store.contacts.unshift(contactToAdd as ContactOverview);
  store.totalContactRecords += 1;
  closeCreateContactModal();
  showSuccess('Contact successfully created', 'Success');
  isSavingContact.value = false;
};

const onShowFiltersChange = (value: boolean) => {
  if (!value) {
    store.hideTableFilters();
  } else {
    store.showTableFilters();
  }
};

const moreMenuRef = ref();

const contactMenuOptions = ref([
  {
    label: 'Actions',
    items: [
      {
        label: 'Add Activity',
        icon: 'pi pi-calendar',
        class: 'pv-menu-item-primary-text',
        command: () => showModal('Task', 'contacts', { id: store.selectedContact.id, name: store.selectedContact.fullName, type: 'Contact' }),
      },
      {
        label: 'Send Email',
        icon: 'pi pi-envelope',
        command: () => onOpenEmailClick(),
      },
      {
        label: 'Call',
        icon: 'pi pi-phone',
        command: () => call(),
      },
      {
        label: 'Combine Contacts',
        icon: 'pi pi-arrow-right-arrow-left',
        command: () => onCombineContactsClick(),
      },
      {
        label: 'Delete',
        icon: 'pi pi-trash',
        command: () => onDeleteContactClick(),
      },
    ],
  },
]);

const onOpenEmailClick = async () => {
  const [email = ''] = store.selectedContact.emails.split(',')
    .map(e => e.trim())
    .filter(Boolean);
  const fullName = isEmptyString(store.selectedContact.fullName) ? 'Contact' : store.selectedContact.fullName;
  if (isEmptyString(email)) {
    showInfo(`No email found for ${fullName} - please add an email.`);
    await new Promise(resolve => setTimeout(resolve, 1500));
  }
  postEmailActivityUnlessSynced(1, store.selectedContact.id, store.selectedContact.fullName, 'contacts');
  window.open(`mailto:${email}`);
};

const dial = (number: string) => {
  const phone = number.replace(/[^\d+]/g, '');
  postActivity(2, store.selectedContact.id, store.selectedContact.fullName, 'contacts');
  window.location.href = `tel:${phone}`;
};

const call = () => {
  if (isEmptyString(store.selectedContact.phoneNumbers)) {
    const contact = isEmptyString(store.selectedContact.fullName) ? 'Contact' : store.selectedContact.fullName;
    showInfo(`Please add phone number for ${contact}`);

    return;
  }

  const [phoneNumber = ''] = store.selectedContact.phoneNumbers.split(',')
    .map(number => number.trim())
    .filter(Boolean);

  if (isEmptyString(phoneNumber)) {
    const contact = isEmptyString(store.selectedContact.fullName) ? 'Contact' : store.selectedContact.fullName;
    showInfo(`Please add phone number for ${contact}`);

    return;
  }

  dial(phoneNumber);
};

const onContactActionsButtonClick = (data, e: MouseEvent) => {
  store.selectedContact = data;
  moreMenuRef.value.toggle(e);
};

const onDeleteContactClick = () => {
  confirm.require({
    group: 'contactDelete',
    accept: () => onDeleteContactConfirm(),
    position: 'center',
    message: 'Are you sure you want to delete this contact record and unassociate it from any relationships?',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Yes',
    rejectLabel: 'No',
    rejectClass: 'p-button-secondary',
    acceptProps: {
      dt: greenButtonTheme,
    },
  });
};

const onDeleteContactConfirm = async () => {
  await deleteContact();
};

const showCombineContactsWizard = ref(false);
const combineSourceContact = ref<ContactOverview | null>(null);

const onCombineContactsClick = () => {
  combineSourceContact.value = store.selectedContact;
  showCombineContactsWizard.value = true;
};

const onContactsMerged = () => {
  showCombineContactsWizard.value = false;
  showSuccess('Contacts successfully combined', 'Success');
  loadContactsHome(true);
};

const showCreateLeadWizard = ref(false);
const leadWizardHasUnsavedChanges = ref(false);

const wizardsHaveUnsavedChanges = () => opportunityStore.showOpportunityCreationModal
  ? creationStore.hasUnsavedChanges()
  : leadWizardHasUnsavedChanges.value;

const { component: UnsavedChanges, onClick: handleCloseWizard } = useConfirmUnsavedChanges(wizardsHaveUnsavedChanges, () => {
  showCreateLeadWizard.value = false;
  if (opportunityStore.showOpportunityCreationModal) {
    closeOpportunityCreationModal();
  }
}, 'dialog');

const suppressWizardWarning = ref(false);

const onModalClose = () => {
  if (suppressWizardWarning.value) {
    closeOpportunityCreationModal();
  } else {
    handleCloseWizard();
  }
};

const onCampaignWizardClose = async () => {
  suppressWizardWarning.value = true;
  await nextTick();

  onModalClose();
};

const closeOpportunityCreationModal = () => {
  opportunityStore.showOpportunityCreationModal = false;
  suppressWizardWarning.value = false;
};

</script>
<template>
  <AppContainer>
    <FormSkeleton v-if="store.isLoading" />
    <template v-else>
      <div class="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div class="flex items-center gap-2">
          <Avatar
            icon="pi pi-id-card"
            size="normal"
            shape="circle"
            style="background-color: var(--p-fuchsia-100); color: var(--p-fuchsia-500);"
          />
          <h1 class="text-lg">
            Contacts
          </h1>
          <Badge :value="store.totalContactRecords" size="large" severity="secondary" />
        </div>
        <div class="flex flex-col gap-4 sm:flex-row sm:gap-4">
          <Button
            v-tooltip.left="'Export'"
            size="small"
            severity="secondary"
            @click="exportCSV()"
          >
            <i class="pi pi-file-export" /> <span style="font-weight:700;">
              Export
            </span>
          </Button>
          <Button
            v-tooltip.left="'Contact'"
            size="small"
            @click="onAddContactClick()"
          >
            <i class="pi pi-pencil" /> <span style="font-weight:700;">
              Create Contact
            </span>
          </Button>
        </div>
      </div>
      <div class="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div class="flex items-center justify-center gap-2">
          <span class="text-center">Filters</span>
          <ToggleSwitch v-model="store.showFilters" @update:model-value="val => onShowFiltersChange(val)" />
          <Button
            v-tooltip.left="'Clear Filters'"
            size="small"
            outlined
            severity="secondary"
            class="flex items-center"
            :disabled="!(store.showFilters && store.isFilterChanged)"
            @click="clearFilters"
          >
            <i class="pi pi-filter-slash" />
            <span class="font-bold">Clear Filters</span>
          </Button>
        </div>
        <div class="flex items-center justify-center gap-2">
          <ColumnDisplaySelector
            :default-table-columns="defaultColumns"
            :default-visible-columns="defaultColumns.filter(col => col.visible).map(col => col.field)"
            :table-columns="columnOrder"
            :visible-columns="visibleColumns"
            storage-prefix="crm:contacts"
            @update:visible-columns="handleVisibleColumnChange"
            @update:table-columns="handleColumnOrderChange"
          />
        </div>
      </div>
      <HeightOptimizer>
        <template #default="{availableHeight}">
          <DataTable
            ref="dt"
            v-model:filters="store.tableFilters"
            lazy
            :value="store.contacts"
            :loading="store.isTableLoading"
            :total-records="store.totalContactRecords"
            :rows="store.rowsPerPage"
            paginator
            :rows-per-page-options="rowsPerPageOptions"
            :filter-display="store.showFilters ? 'row' : null"
            data-key="id"
            table-style="min-width: 50rem;"
            :virtual-scroller-options="{ itemSize: 50 }"
            :scroll-height="`calc(${availableHeight} - 60px)`"
            table-layout="fixed"
            scrollable
            reorderable-columns
            resizable-columns
            column-resize-mode="expand"
            @row-click="onRowClick"
            @filter="onFilter"
            @sort="onSort"
            @page="onPage"
          >
            <Column
              field="id"
              :header="defaultColumns.find(col => col.field === 'id').header"
              sortable
              :hidden="!visibleColumns.includes('id')"
              :reorderable-column="false"
              :filter-match-mode-options="[
                { label: 'Contains', value: 'contains' },
                { label: 'Equals', value: 'equals' },
                { label: 'Less Than', value: 'lt' },
                { label: 'Greater Than', value: 'gt' },
              ]"
              style="width: 0px"
            >
              <template #filter="{ filterModel, filterCallback }">
                <InputText
                  v-show="store.showFilters"
                  v-model="filterModel.value"
                  type="text"
                  class="p-column-filter"
                  placeholder="Ids"
                  style="min-width: 8rem; max-width: 8rem"
                  @input="filterCallback()"
                />
              </template>
            </Column>
            <Column
              field="fullName"
              :header="defaultColumns.find(col => col.field === 'fullName').header"
              sortable
              :hidden="!visibleColumns.includes('fullName')"
              :reorderable-column="false"
              :filter-match-mode-options="[
                { label: 'Contains', value: 'contains' },
                { label: 'Starts With', value: 'startsWith' },
                { label: 'Unnamed', value: 'isEmpty' },
              ]"
              class="w-2/12"
            >
              <template #filter="{ filterModel, filterCallback }">
                <InputText
                  v-show="store.showFilters"
                  v-model="filterModel.value"
                  type="text"
                  class="p-column-filter"
                  placeholder="Contacts"
                  style="min-width: 8rem; max-width: 12rem"
                  @input="filterCallback()"
                />
              </template>
              <template #body="slotProps">
                <span class="font-semibold">{{ slotProps.data.fullName }}</span>
              </template>
            </Column>
            <Column
              field="company"
              :header="defaultColumns.find(col => col.field === 'company').header"
              sortable
              :hidden="!visibleColumns.includes('company')"
              :reorderable-column="false"
              :filter-match-mode-options="[
                { label: 'Contains', value: 'contains' },
                { label: 'Starts With', value: 'startsWith' },
              ]"
              class="w-3/12"
            >
              <template #filter="{ filterModel, filterCallback }">
                <InputText
                  v-show="store.showFilters"
                  v-model="filterModel.value"
                  type="text"
                  class="p-column-filter"
                  placeholder="Companies"
                  style="min-width: 8rem; max-width: 12rem"
                  @input="filterCallback()"
                />
              </template>
            </Column>
            <Column
              field="ownerName"
              :header="defaultColumns.find(col => col.field === 'ownerName').header"
              sortable
              :hidden="!visibleColumns.includes('ownerName')"
              :reorderable-column="false"
              :show-filter-menu="false"
              class="w-1/12"
            >
              <template #filter="{ filterModel, filterCallback }">
                <MultiSelect
                  v-show="store.showFilters"
                  v-model="filterModel.value"
                  :options="store.contactOwners"
                  filter
                  placeholder="Owners"
                  class="p-column-filter"
                  style="min-width: 8rem; max-width: 12rem"
                  :show-clear="true"
                  @change="filterCallback()"
                />
              </template>
            </Column>
            <Column
              field="address"
              :header="defaultColumns.find(col => col.field === 'address').header"
              sortable
              :hidden="!visibleColumns.includes('address')"
              :reorderable-column="false"
              :show-filter-menu="false"
              class="w-4/12"
            >
              <template #filter="{ filterModel, filterCallback }">
                <InputText
                  v-show="store.showFilters"
                  v-model="filterModel.value"
                  type="text"
                  class="p-column-filter"
                  placeholder="Addresses"
                  style="min-width: 8rem; max-width: 12rem"
                  @input="filterCallback()"
                />
              </template>
            </Column>
            <Column
              field="emails"
              :header="defaultColumns.find(col => col.field === 'emails').header"
              sortable
              :hidden="!visibleColumns.includes('emails')"
              :reorderable-column="false"
              :show-filter-menu="false"
              class="w-2/12"
            >
              <template v-if="store.showFilters" #filter="{ filterModel, filterCallback }">
                <InputText
                  v-model="filterModel.value"
                  type="text"
                  class="p-column-filter"
                  placeholder="Emails"
                  style="min-width: 8rem; max-width: 12rem"
                  @input="filterCallback()"
                />
              </template>
            </Column>
            <Column
              field="phoneNumbers"
              :header="defaultColumns.find(col => col.field === 'phoneNumbers').header"
              sortable
              :hidden="!visibleColumns.includes('phoneNumbers')"
              :reorderable-column="false"
              :show-filter-menu="false"
              class="w-2/12"
            >
              <template v-if="store.showFilters" #filter="{ filterModel, filterCallback }">
                <InputText
                  v-model="filterModel.value"
                  type="text"
                  class="p-column-filter"
                  placeholder="Phone Numbers"
                  style="min-width: 8rem; max-width: 12rem"
                  @input="filterCallback()"
                />
              </template>
            </Column>
            <Column
              field="lastActivityDate"
              :header="defaultColumns.find(col => col.field === 'lastActivityDate').header"
              sortable
              :hidden="!visibleColumns.includes('lastActivityDate')"
              :reorderable-column="false"
              :sort-field="'lastActivityTimeStamp'"
            />
            <Column
              class="w-1/12"
              :reorderable-column="false"
              frozen
              align-frozen="right"
            >
              <template #body="slotProps">
                <Button
                  v-tooltip.left="'Maintenance'"
                  size="small"
                  rounded
                  class="icon-button"
                  style="background:none; border:none; color:var(--primary-blue)"
                  @click="(event) => onContactActionsButtonClick(slotProps.data, event)"
                >
                  <i class="pi pi-ellipsis-h" style="font-size: 1.5rem; color:var(--apx-dark-gray-400)" />
                </Button>
              </template>
            </Column>
          </DataTable>
        </template>
      </HeightOptimizer>
    </template>
    <Menu ref="moreMenuRef" :model="contactMenuOptions" :popup="true" />
  </AppContainer>
  <RouterView />
  <Modal
    title="Convert Lead to Opportunity"
    :visible="opportunityStore.showOpportunityCreationModal"
    disable-footer
    @close="onModalClose"
  >
    <template #body>
      <CreateCampaignWizard
        :industry-code-required="industryCodeRequired"
        type="Opportunity"
        :edit-lead="opportunityStore.selectedOpportunity['Lead']"
        @close="onCampaignWizardClose"
        @update:has-unsaved-changes="hasChanges => leadWizardHasUnsavedChanges = hasChanges"
      />
    </template>
  </Modal>
  <Modal
    title="Combine Contacts"
    :visible="showCombineContactsWizard"
    disable-footer
    @close="showCombineContactsWizard = false"
  >
    <template #body>
      <CombineContactsWizard
        v-if="showCombineContactsWizard && combineSourceContact"
        :source-contact-id="combineSourceContact.id"
        :source-contact-name="combineSourceContact.fullName"
        @close="showCombineContactsWizard = false"
        @merged="onContactsMerged"
      />
    </template>
  </Modal>
  <Modal
    title="Create Contact"
    :visible="showCreateContactModal"
    disable-footer
    @close="closeCreateContactModal"
  >
    <template #body>
      <div class="flex p-4 w-full lg:w-2/3 lg:mr-auto lg:ml-auto">
        <CreateContact :is-loading="isSavingContact" @on:submit="onContactCreated" />
      </div>
    </template>
  </Modal>
  <ConfirmDialog group="contactDelete" />
  <component :is="addActivityComponent" />
  <component :is="UnsavedChanges" />
</template>

<style scoped>
.datatable-container {
  /* Ensure the container occupies full height */
  height: 100vh;
  display: flex;
  flex-direction: column;
}

:deep(.p-datatable-header) {
    background: none;
}

.p-button-secondary {
  background-color: lightgray;
  background: var(--white);
  border: 1px solid var(--black-16);
  color: var(--black-80);
}

.p-badge-secondary {
  background-color: lightgray;
  background: var(--white);
  border: 1px solid var(--black-16);
  color: var(--black-80);
}
</style>
